/**
 * MutableEdge - Custom edge with disable toggle button
 *
 * Shows a disable button in the middle of the connection:
 * - Always visible when disabled
 * - Appears on hover when not disabled
 */

import React, { useState, CSSProperties } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
  type EdgeProps,
  type Edge
} from '@xyflow/react';
import { useScope } from '../../contexts/ScopeContext';
import { useRuntime } from '../../contexts/RuntimeProvider';

interface MutableEdgeData extends Record<string, unknown> {
  disabled?: boolean;
}

type MutableEdge = Edge<MutableEdgeData>;

export function MutableEdge(props: EdgeProps<MutableEdge>) {
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
  } = props;
  const id = props.id as string;
  const style = (props.style ?? {}) as CSSProperties;
  const data = props.data as MutableEdgeData | undefined;
  const [isHovered, setIsHovered] = useState(false);
  const { instanceId } = useScope();
  const { toggleConnectionDisabled, removeConnection } = useRuntime();
  const { setEdges } = useReactFlow();

  const disabled = data?.disabled ?? false;

  const handleClick = () => {
    // Select this edge
    setEdges(edges => edges.map(e => ({ ...e, selected: e.id === id })));
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    const [source, target] = id.split('->');
    if (source && target) {
      removeConnection(instanceId, source, target);
    }
  };

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition
  });

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleConnectionDisabled(instanceId, id);
  };

  // Show button if disabled OR if hovered
  const showButton = disabled || isHovered;

  return (
    <>
      {/* Invisible wider path for hover detection and click selection */}
      <path
        d={edgePath}
        fill="none"
        strokeWidth={40}
        stroke="transparent"
        style={{ cursor: 'pointer' }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      {/* Visible edge */}
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        interactionWidth={20}
        style={{
          ...style,
          // When disabled, override opacity; otherwise preserve activity-based opacity from style
          ...(disabled ? { opacity: 0.3, strokeDasharray: '5,5' } : {})
        }}
      />
      {/* Connection controls (disable toggle + delete) */}
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            opacity: showButton ? 1 : 0,
            transition: 'opacity 0.15s ease',
            display: 'flex',
            gap: 4
          }}
          className="nodrag nopan"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={handleToggle}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: 'none',
              background: disabled ? '#ef4444' : '#374151',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
            }}
            title={disabled ? 'Enable connection' : 'Disable connection'}
          >
            {disabled ? '⊘' : '●'}
          </button>
          <button
            onClick={handleDelete}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              border: 'none',
              background: '#374151',
              color: '#ef4444',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              boxShadow: '0 2px 6px rgba(0,0,0,0.4)'
            }}
            title="Delete connection"
          >
            ✕
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
